import { FormikProps, getIn } from "formik";
import * as Yup from "yup";

const insuranceIdentifierSchema = (label: string) =>
  Yup.string()
    .trim()
    .nullable()
    .label(label)
    .matches(/^[a-zA-Z0-9]*$/, `${label} must be alphanumeric`)
    .max(50, `${label} cannot exceed 50 characters`);

export const INSURANCE_IDENTIFIER_SHAPE = {
  insuranceId: insuranceIdentifierSchema("Insurance ID"),
  memberId: insuranceIdentifierSchema("Member ID"),
  groupId: insuranceIdentifierSchema("Group ID"),
};

const requiredWhenRelationshipSelected = <T extends Yup.Schema>(
  schema: T,
  label: string
) =>
  schema.when("insuredRelationshipWithPatient", {
    is: (relationship?: string | null) => !!relationship,
    then: (field) => field.required(`${label} is required`),
  });

const subscriberRequired = (label: string) =>
  Yup.string().trim().nullable().label(label).required(`${label} is required`);

const subscriberTextSchema = (label: string) =>
  requiredWhenRelationshipSelected(
    Yup.string().trim().nullable().label(label),
    label
  );

export const SUBSCRIBER_SHAPE = {
  subscriberFirstName: subscriberTextSchema("First Name"),
  subscriberLastName: subscriberTextSchema("Last Name"),
  subscriberBirthDate: requiredWhenRelationshipSelected(
    Yup.mixed().nullable(),
    "Date of Birth"
  ),
  subscriberGender: subscriberTextSchema("Gender"),
  subscriberAddress: Yup.object({
    zipcode: Yup.string()
      .nullable()
      .matches(
        /^\d{5}(-\d{4})?$/,
        "Enter a valid ZIP Code (e.g., 12345 or 12345-6789)"
      ),
  })
    .transform((address) => address ?? {})
    .when("insuredRelationshipWithPatient", {
      is: (relationship?: string | null) => !!relationship,
      then: (address) =>
        address.shape({
          line1: subscriberRequired("Address Line 1"),
          city: subscriberRequired("City"),
          state: subscriberRequired("State"),
          country: subscriberRequired("Country"),
          zipcode: subscriberRequired("ZIP Code").matches(
            /^\d{5}(-\d{4})?$/,
            "Enter a valid ZIP Code (e.g., 12345 or 12345-6789)"
          ),
        }),
    }),
};

export type InsuranceIdentifierField = keyof typeof INSURANCE_IDENTIFIER_SHAPE;

export type SubscriberField =
  | Exclude<keyof typeof SUBSCRIBER_SHAPE, "subscriberAddress">
  | `subscriberAddress.${"line1" | "city" | "state" | "country" | "zipcode"}`;

export const getInsuranceFieldError = <Values>(
  formik: Pick<FormikProps<Values>, "touched" | "errors" | "submitCount">,
  index: number,
  field: InsuranceIdentifierField | SubscriberField
): string | undefined => {
  const path = `patientInsurances[${index}].${field}`;
  if (!formik.submitCount && !getIn(formik.touched, path)) return undefined;
  const error: unknown = getIn(formik.errors, path);
  return typeof error === "string" ? error : undefined;
};
